<!-- eslint-disable vue/no-deprecated-v-bind-sync -->
<template>
  <div>
    <v-row v-show="!loading">
      <v-col cols="8">
        <!-- Options to resize iframe -->
        <div class="d-flex justify-end">
          <div class="justify-start flex-grow-1" style="max-height: 20px;">
            <div class="mt-1 mb-1">
              <v-select
                v-if="seoPreviewWorkspacesList"
                v-model="seoContentWorkspaceRef"
                class="select-preview-workspace"
                :items="seoPreviewWorkspacesList"
                item-text="name"
                item-value="code"
                :menu-props="{ bottom: true, offsetY: true }"
                color="white"
                label="Workspace Preview"
                dense
                outlined
                mandatory
                hide-details
              />
            </div>
          </div>
          <v-btn-toggle
            v-model="iframeMode"
            class="justify-end"
            mandatory
            background-color="#343434"
            dense
          >
            <v-btn id="iframeModes-phone" class="iframeModes mt-1 mb-1" value="phone" @click="changeIframeMode('phone')">
              <v-icon>phone_iphone</v-icon>
            </v-btn>

            <v-btn id="iframeModes-tablet" class="iframeModes mt-1 mb-1" value="tablet" @click="changeIframeMode('tablet')">
              <v-icon>tablet</v-icon>
            </v-btn>

            <v-btn id="iframeModes-laptop" class="iframeModes mt-1 mb-1" value="laptop" @click="changeIframeMode('laptop')">
              <v-icon>laptop</v-icon>
            </v-btn>
          </v-btn-toggle>
        </div>

        <div style="background-color: rgba(0,0,0,0.3); line-height: 0px; height: calc(100vh - 252px);" class="">
          <!-- <div> -->
          <iframe v-show="!newLoading" id="resp-iframe" :src="previewURL" class="resp-seo-iframe" title="iframe SEO example 1" @load="iframeLoaded"></iframe>

          <v-progress-circular
            v-if="newLoading"
            class="iframe-loader"
            size="85"
            width="7"
            color="grey"
            indeterminate
          />
        </div>
      </v-col>
      <v-col v-if="selectedModule.active" cols="4">
        <v-card color="secondary" class="border-radius-0">
          <v-row class="mx-0 grey darken-3">
            <v-card-title>
              <h2>{{ selectedModule.moduleName }}</h2>
            </v-card-title>
          </v-row>
          <GenerateForm
            v-if="!newLoading"
            class="side-form"
            :fields-to-show="selectedModule.fieldsToShow"
            :raw-content-types="rawSEOContentTypes"
            :content-valid.sync="contentValidRef"
            :content.sync="seoContent"
            :content-type="seoContentType"
            :show-panel-labels="false"
          />
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>

import gql from 'graphql-tag';

import { ref, computed, watch, onUnmounted } from 'vue';
import { useRoute } from 'vue2-helpers/vue-router';

import { useStore } from 'vue2-helpers/vuex';
import { useApolloClient } from '@vue/apollo-composable';
import _ from 'lodash';
import { difference } from '@/utils/content';

import { paths, generateContentQueryMapping, createRelatedContentQueries, createRelatedContentQueriesFromMapping, remapContentToQueryFields } from '@/utils/editor';
import { changeIframeMode } from '@/utils/cms';

const { resolveClient } = useApolloClient();

const store = useStore();
const route = useRoute();

const newLoading = ref(true);

const isSpeedboat = computed(() => store.state.environment.PROJECT === 'speedboat');

const previewContentTypeQueryMapping = ref(null);

// eslint-disable-next-line no-undef
const props = defineProps({
  contentType: Object,
  contentValid: Boolean,
  content: Object,
  seoContent: Object,
  seoContentWorkspace: String,
  loading: Boolean,
  seoPreviewWorkspacesList: Array
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['update:loading', 'update:contentValid', 'update:seoContent', 'update:seoContentWorkspace']);

const rawSEOContentTypes = ref({});

const seoContentType = ref({});

const seoContentWorkspaceRef = ref(props.seoContentWorkspace);

watch(seoContentWorkspaceRef, val => {
  emit('update:seoContentWorkspace', val);
});

const generateSEOContentType = (contentType, path = '') => {
  const newSeoContentType = {
    name: `SEO ${contentType.name}`,
    configs: {},
    // Needs panelOrder for getPanels() in GenerateForm
    panelOrder: contentType.panelOrder,
    fields: {}
  };

  if (contentType.configs?.wtype) newSeoContentType.configs.type = `SEO_${contentType.configs.wtype}`;
  if (contentType.configs?.type) newSeoContentType.configs.type = `SEO_${contentType.configs.type}`;

  const excludedFieldProperties = ['mandatory'];

  Object.keys(contentType.fields).forEach(key => {
    let newPath = path ? `${path}${key}` : key;

    let field = null;

    switch (contentType.fields[key].viewType) {
      case 'Text':
      case 'Textarea':
        if (!Object.keys(previewContentTypeQueryMapping.value).some(queryMap => queryMap.startsWith(`${newPath}`))) {
          break;
        }

        field = { ...contentType.fields[key] };
        break;
      case 'Entity':
        // Multivalued case
        // If is multivalued the value in the mapping as a "[]"
        if (contentType.fields[key].hasOwnProperty('multivalued') && contentType.fields[key].multivalued === true) {
          newPath = `${newPath}[]`;
        }

        newPath = `${newPath}.`;

        if (!Object.keys(previewContentTypeQueryMapping.value).some(queryMap => queryMap.startsWith(`${newPath}`))) {
          break;
        }

        field = { ...contentType.fields[key] };

        // Multivalued case
        if (contentType.fields[key].hasOwnProperty('multivalued') && contentType.fields[key].multivalued === true) {
          field.multivalued = true;
        }

        // Find original contentType
        const picketEntityName = Object.keys(store.state.rawContentTypes).find(aux => store.state.rawContentTypes[aux].configs.type === contentType.fields[key].type);

        field.type = `SEO_${store.state.rawContentTypes[picketEntityName].configs.type}`;

        // Entity are treated like a content type
        field = { ...field, ...generateSEOContentType(store.state.rawContentTypes[picketEntityName], newPath) };

        break;
      case 'Picker':

        // Multivalued case
        // If is multivalued the value in the mapping as a "[]"
        if (contentType.fields[key].hasOwnProperty('multivalued') && contentType.fields[key].multivalued === true) {
          newPath = `${newPath}[]`;
        }

        newPath = `${newPath}.`;

        if (!Object.keys(previewContentTypeQueryMapping.value).some(queryMap => queryMap.startsWith(`${newPath}`))) {
          break;
        }

        field = { ...contentType.fields[key] };

        field.viewType = 'Entity';

        // Multivalued case
        if (contentType.fields[key].hasOwnProperty('multivalued') && contentType.fields[key].multivalued === true) {
          field.multivalued = true;
        }

        // Find original contentType
        const picketContentTypeName = Object.keys(store.state.rawContentTypes).find(aux => store.state.rawContentTypes[aux].configs.wtype === contentType.fields[key].contentWtype);

        field.type = `SEO_${store.state.rawContentTypes[picketContentTypeName].configs.type}`;

        field = { ...field, ...generateSEOContentType(store.state.rawContentTypes[picketContentTypeName], newPath) };

        break;
      default:
        return null;
    }

    if (field) {
      // delete unncessary properties
      excludedFieldProperties.forEach(property => {
        delete field[property];
      });

      newSeoContentType.fields[key] = field;
    }
  });

  rawSEOContentTypes.value[newSeoContentType.name] = newSeoContentType;

  return newSeoContentType;
};

const iframeMode = ref('laptop');

const sendInformation = info => {
  info.preview = true;
  document.getElementById('resp-iframe').contentWindow.postMessage(info, '*');
  console.log('Send information', info);
};

const contentValidRef = ref(props.contentValid);

watch(contentValidRef, val => {
  emit('update:contentValid', val);
});

/** ****************************
    PICKERS
  **************************** */

const relationContents = ref([]);

const findContentByUuid = uuid => relationContents.value.find(p => p.uuid === uuid) || null;

const searchRelatedContent = async relations => {
  let relationContentQueries = [];

  // Only search relations that we didn't already retrieved
  const filteredRelations = relations.filter(relation => !findContentByUuid(relation.uuid));

  if (previewContentTypeQueryMapping.value) {
    relationContentQueries = createRelatedContentQueriesFromMapping(filteredRelations, previewContentTypeQueryMapping.value, store.state.rawContentTypes);
  } else {
    relationContentQueries = createRelatedContentQueries(filteredRelations, store.state.rawContentTypes);
  }

  if (relationContentQueries.length) {
    const getPickersQuery = `query { ${relationContentQueries.join(' ')} }`;
    const ApolloClient = resolveClient();

    try {
      const result = await ApolloClient.query({ query: gql(getPickersQuery), fetchPolicy: 'no-cache', errorPolicy: 'ignore', notifyOnNetworkStatusChange: true });

      if (result.data && Object.values(result.data).length) {
        relationContents.value = relationContents.value.concat(Object.values(result.data));
      }
    } catch (error) {
      console.log('[ERROR] getPickersQuery: ', error);
    }
  }
};

const prepareFields = async (contentWithSEO, content, fields) => {
  content = _.cloneDeep(content);
  contentWithSEO = _.cloneDeep(contentWithSEO);

  const fieldsToSend = {};

  const mergedContent = _.mergeWith(
    {},
    content,
    contentWithSEO,
    (a, b) => ((b === null) ? a : undefined)
  );

  fields.forEach(fieldKey => {
    if (mergedContent && mergedContent[fieldKey]) {
      fieldsToSend[fieldKey] = mergedContent[fieldKey];
    }
  });

  const contentPaths = paths(fieldsToSend).filter(p => p.includes('.uuid') && !p.includes('parent.'));
  const relations = [];
  contentPaths.forEach(path => {
    const uuid = _.get(content, path);
    const wtype = _.get(content, path.replace('.uuid', '.wtype'));

    // Remove array references from the path and split it
    const relationPath = path.replace('.uuid', '').replace(/\[.*?\]/g, '').split(/[\.\[\]]/);

    if (wtype) { // if it has wtype it means it is a contentType and we must get its data
      relations.push({ path, uuid, wtype, relationPath });
    } else {
      console.log(uuid, ' is not a content type because it does not have wtype associated');
    }
  });

  await searchRelatedContent(relations);

  // First request
  if (!contentWithSEO) {
    relations.forEach(r => {
      const relationContent = findContentByUuid(r.uuid);

      if (relationContent) {
        const pathContentToReplace = r.path.replace('.uuid', '');
        _.set(fieldsToSend, pathContentToReplace, relationContent);
      }
    });
  } else {
    // Replace fields with seo content
    relations.forEach(r => {
      let relationContent = findContentByUuid(r.uuid);

      const pathContentToReplace = r.path.replace('.uuid', '');

      const seoRelationContent = _.get(contentWithSEO, pathContentToReplace);

      // To not override the content with null values on seoContent
      relationContent = _.mergeWith(
        {},
        relationContent,
        seoRelationContent,
        (a, b) => ((b === null) ? a : undefined)
      );

      if (relationContent) {
        _.set(fieldsToSend, pathContentToReplace, relationContent);
      }
    });
  }
  if (previewContentTypeQueryMapping.value) {
    return remapContentToQueryFields(previewContentTypeQueryMapping.value, fieldsToSend);
  }

  return fieldsToSend;
};

/* ********************************************************************** */

const clonedContent = computed(() => JSON.parse(JSON.stringify(props.seoContent)));

const sendIframeFields = async fieldsToBeSent => {
  sendInformation({ loading: true });
  const fields = await prepareFields({ ...props.seoContent }, { ...props.content }, fieldsToBeSent);
  sendInformation({ fields: fields });
};

watch(
  clonedContent,
  async (newVal, oldVal) => {
    const fieldsToBeSent = Object.keys(difference(newVal, oldVal));

    await sendIframeFields(fieldsToBeSent);
  },
  { deep: true }
);

const selectedModule = ref({
  active: false,
  moduleName: null,
  fieldsToShow: [],
  sectionID: null
});

const iframeLoaded = async () => {
  // ON Alpha we need to send references
  // Request content type query
  sendInformation({ references: store.state.references || {} });
  newLoading.value = false;

  if (!isSpeedboat.value) {
    const fieldsToBeSent = Object.keys(props.content);

    const fields = await prepareFields(null, props.content, fieldsToBeSent);
    sendInformation({ fields: fields });
  }
};

// Receives information
const informationReceiver = async event => {
  if (event.data.contentTypeQuery) {
    // Update iframe fields when content type query is received
    previewContentTypeQueryMapping.value = generateContentQueryMapping(event.data.contentTypeQuery, props.contentType, store.state.rawContentTypes);
  }

  // Case user unselected a section
  if (event.data.hasOwnProperty('unselectModule')) {
    console.log('informationReceiver unselectModule');
    selectedModule.value.active = false;
  }

  // In case is inline editing
  if (event.data.hasOwnProperty('inlineFields')) {
    console.log('informationReceiver inlineFields');
    selectedModule.value.active = true;
    selectedModule.value.moduleName = event.data.moduleName;

    selectedModule.value.fieldsToShow = !event.data.inlineFields ? [] : event.data.inlineFields;
  }
};

window.addEventListener('message', informationReceiver, false);

onUnmounted(() => {
  window.removeEventListener('message', informationReceiver, false);
});

const previewURL = computed(() => {
  const previewConf = store.getters.workspace.settings.VUE_APP_PREVIEW;

  const uuid = previewConf.uuid ? `/${route.params.uuid}` : '';
  const version = previewConf.version ? `/${store.getters.version.name}` : '';
  const mode = previewConf.mode === 'live' ? '/live' : '/draft';
  let locale = '';

  if (previewConf.locale) {
    if (store.getters.locale) {
      locale = `/${store.getters.locale}`;
    } else {
      locale = `/${previewConf.defaultPreviewLocale}`;
    }
  }

  return `${previewConf.url}/preview/${props.seoContentWorkspace}/${route.params.wtype}${uuid}${version}${mode}${locale}`;
});

watch(
  previewURL,
  async (newVal, oldVal) => {
    newLoading.value = true;
  }
);

watch(
  previewContentTypeQueryMapping,
  async (newVal, oldVal) => {
    seoContentType.value = generateSEOContentType(props.contentType);
    // Get all fields to send
    const queryFieldsToSend = Object.keys(props.content);

    await sendIframeFields(queryFieldsToSend);
  }
);

</script>

<style scoped>
.select-preview-workspace {
  height: 39px;
  width: 250px;
}

.iframe-loader{
  position: relative;
  top:50%;
  left:45%;
}

.resp-seo-iframe {
  height: calc(100vh - 252px);
  width: 100%;
  border: 0
}

</style>
